// Press & Reviews page - Terminal-style press clippings display
// Fictional press excerpts from invented publications presented with
// terminal aesthetics, hover effects, and staggered animations.
// Reviews randomly mutate to different book variants over time.

import { gsap } from 'gsap';
import {
  getInitialReviews,
  getAllReviews,
  setupReviewMutation
} from '../scripts/content-randomizer';
import { appendReturnToHouse } from '../scripts/navigation';
import { glitchText } from '../scripts/effects';
import { prefersReducedMotion } from '../scripts/animations';

interface Review {
  quote: string;
  source: string;
  author?: string;
}

// Track cleanup functions
let cleanupFunctions: (() => void)[] = [];

/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Render the press page
 * Terminal-style display of fictional press reviews with animations
 */
export function renderPress(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';
  cleanupFunctions = [];

  const container = document.createElement('div');
  container.className = 'press-container';

  // Get randomized initial reviews from the pool
  const reviews = getInitialReviews(7);
  const allReviews = getAllReviews();

  // Track pool indices for each displayed review
  const poolIndices = reviews.map(review =>
    allReviews.findIndex(r => r.quote === review.quote)
  );

  // Build review items with proper indexing and keyboard navigation
  const reviewCount = reviews.length;
  const reviewItems = reviews
    .map((review, index) => {
      const authorAttribution = review.author
        ? `<span class="press-author">${escapeHtml(review.author)}, </span>`
        : '';

      return `
        <article class="press-item" data-index="${index}" data-pool-index="${poolIndices[index]}" tabindex="0" role="listitem"
                 aria-label="Review ${index + 1} of ${reviewCount}: ${escapeHtml(review.quote.substring(0, 50))}...">
          <blockquote class="press-quote">
            <span class="press-quote-text">${escapeHtml(review.quote)}</span>
          </blockquote>
          <footer class="press-attribution">
            <span class="press-source">${authorAttribution}${escapeHtml(review.source)}</span>
          </footer>
        </article>
      `;
    })
    .join('<div class="press-divider" aria-hidden="true"></div>');

  container.innerHTML = `
    <div class="press-inner">
      <!-- Terminal box header -->
      <div class="press-terminal-box">
        <header class="press-terminal-header" id="main-content" tabindex="-1">
          PRESS CLIPPINGS
        </header>

        <div class="press-terminal-body">
          <div class="press-list" role="list" aria-label="Press reviews and critical reception">
            ${reviewItems}
          </div>
        </div>
      </div>

      <!-- Mysterious footnote -->
      <footer class="press-footer">
        <p class="press-disclaimer">
          <span class="press-disclaimer-symbol">※</span>
          Authenticity of all clippings unverified. Some sources may not exist outside this document.
        </p>
      </footer>
    </div>
  `;

  app.appendChild(container);

  // Add return to house navigation
  const inner = container.querySelector('.press-inner');
  if (inner) {
    appendReturnToHouse(inner as HTMLElement, { className: 'press-nav' });
  }

  // Setup interactive effects
  setupAnimations(container);
  setupHoverEffects(container);
  setupKeyboardNavigation(container);
  setupLiveMutations(container);
}

/**
 * Setup live content mutations for reviews
 * Reviews will randomly change to different book variants over time
 */
function setupLiveMutations(container: HTMLElement): void {
  const items = container.querySelectorAll('.press-item');

  items.forEach((item) => {
    const el = item as HTMLElement;
    const poolIndex = parseInt(el.dataset.poolIndex || '0', 10);

    const cleanup = setupReviewMutation(el, poolIndex, (newReview: Review, newIndex: number) => {
      // Update the content
      const quoteText = el.querySelector('.press-quote-text');
      const sourceEl = el.querySelector('.press-source');

      if (quoteText) {
        quoteText.textContent = newReview.quote;
      }

      if (sourceEl) {
        const authorAttribution = newReview.author
          ? `<span class="press-author">${escapeHtml(newReview.author)}, </span>`
          : '';
        sourceEl.innerHTML = `${authorAttribution}${escapeHtml(newReview.source)}`;
      }

      // Update pool index for next mutation
      el.dataset.poolIndex = newIndex.toString();
    });

    cleanupFunctions.push(cleanup);
  });
}

/**
 * Setup staggered entrance animations for press items
 */
function setupAnimations(container: HTMLElement): void {
  if (prefersReducedMotion()) return;

  const items = container.querySelectorAll('.press-item');
  const dividers = container.querySelectorAll('.press-divider');

  // Initial state - hidden
  gsap.set(items, { opacity: 0, y: 20 });
  gsap.set(dividers, { opacity: 0, scaleX: 0 });

  // Staggered entrance animation
  const tl = gsap.timeline({ delay: 0.2 });

  tl.to(items, {
    opacity: 1,
    y: 0,
    duration: 0.5,
    ease: 'power2.out',
    stagger: 0.15
  });

  tl.to(dividers, {
    opacity: 1,
    scaleX: 1,
    duration: 0.3,
    ease: 'power2.out',
    stagger: 0.1
  }, '-=0.3');

  // Store cleanup
  cleanupFunctions.push(() => {
    tl.kill();
  });
}

/**
 * Setup keyboard navigation for press items
 * Arrow keys move between items, Home/End jump to first/last
 */
function setupKeyboardNavigation(container: HTMLElement): void {
  const items = Array.from(container.querySelectorAll('.press-item')) as HTMLElement[];

  items.forEach((item, index) => {
    const handleKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          // Move to next item, or wrap to first
          if (index < items.length - 1) {
            items[index + 1].focus();
          } else {
            items[0].focus();
          }
          break;

        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          // Move to previous item, or wrap to last
          if (index > 0) {
            items[index - 1].focus();
          } else {
            items[items.length - 1].focus();
          }
          break;

        case 'Home':
          e.preventDefault();
          items[0].focus();
          break;

        case 'End':
          e.preventDefault();
          items[items.length - 1].focus();
          break;
      }
    };

    item.addEventListener('keydown', handleKeydown);

    cleanupFunctions.push(() => {
      item.removeEventListener('keydown', handleKeydown);
    });
  });
}

/**
 * Setup hover effects for press items
 * Includes subtle glitch effect on source text
 */
function setupHoverEffects(container: HTMLElement): void {
  const items = container.querySelectorAll('.press-item');

  items.forEach(item => {
    const el = item as HTMLElement;
    const source = el.querySelector('.press-source') as HTMLElement;

    let glitchTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleMouseEnter = () => {
      if (prefersReducedMotion() || !source) return;

      // Small chance of glitch effect on hover
      if (Math.random() < 0.3) {
        glitchTimeout = setTimeout(() => {
          glitchText(source, 300);
        }, 100);
      }
    };

    const handleMouseLeave = () => {
      if (glitchTimeout) {
        clearTimeout(glitchTimeout);
        glitchTimeout = null;
      }
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    // Store cleanup
    cleanupFunctions.push(() => {
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
      if (glitchTimeout) {
        clearTimeout(glitchTimeout);
      }
    });
  });
}

/**
 * Cleanup press page - remove event listeners, kill animations
 */
export function cleanupPress(): void {
  cleanupFunctions.forEach(fn => fn());
  cleanupFunctions = [];
}
